import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Sparkles, MapPin, Fuel, Clock, DollarSign, TrendingUp, Loader2,
  RefreshCw, Users, ChevronDown, ChevronRight, CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const RADIUS_OPTIONS = [1, 2, 3, 5];
const TIME_WINDOWS = [
  { key: "morning",   label: "Morning (7am – 11am)" },
  { key: "midday",    label: "Midday (11am – 2pm)" },
  { key: "afternoon", label: "Afternoon (2pm – 5pm)" },
  { key: "evening",   label: "Evening (5pm – 7pm)" },
];

export default function NearbyOpportunitiesCard({ initialDate, onScheduled }) {
  const [date, setDate] = useState(initialDate || todayIso());
  const [radius, setRadius] = useState(5);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [scheduling, setScheduling] = useState({}); // user_id -> bool

  const fetchOpportunities = useCallback(async (d, r) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/nearby-opportunities", { params: { date: d, radius_miles: r } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load nearby opportunities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOpportunities(date, radius); }, [date, radius, fetchOpportunities]);

  const handleSchedule = async (opp, opts = {}) => {
    setScheduling((s) => ({ ...s, [opp.user_id]: true }));
    try {
      const body = {
        date: opts.date || opp.suggested_date,
        preferred_time_window: opts.window || opp.suggested_time_window || "morning",
        service_key: opp.service_key,
        square_feet: opp.square_feet,
        yard_area: opp.yard_area,
        frequency: opp.frequency || "one_time",
      };
      const res = await api.post(`/admin/nearby-opportunities/${opp.user_id}/schedule`, body);
      toast.success(`${opp.name} added to ${body.date} · $${res.data.price?.toFixed?.(0) || res.data.price}`);
      onScheduled?.(res.data);
      // refresh
      fetchOpportunities(date, radius);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to route");
    } finally {
      setScheduling((s) => ({ ...s, [opp.user_id]: false }));
    }
  };

  const impact = data?.route_impact_if_all_added || {};
  const opps = data?.opportunities || [];
  const nbhs = data?.neighborhoods || [];

  return (
    <div className="bg-gradient-to-br from-amber-50 via-white to-emerald-50 border border-amber-200 rounded-2xl p-5 shadow-sm" data-testid="nearby-opportunities-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button onClick={() => setExpanded((v) => !v)} className="flex items-start gap-3 text-left" data-testid="nearby-toggle">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shrink-0 shadow-md">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-amber-700">Route Density Booster</div>
              {expanded ? <ChevronDown className="w-3.5 h-3.5 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-700" />}
            </div>
            <h3 className="text-xl font-extrabold tracking-tight font-[Outfit] text-gray-900">Nearby Customer Opportunities</h3>
            <p className="text-sm text-gray-600">Customers within {radius} mi of today&apos;s route — one tap to fill empty time slots.</p>
          </div>
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 w-[155px] text-sm font-bold bg-white"
            data-testid="nearby-date-picker"
          />
          <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
            <SelectTrigger className="h-9 w-[110px] bg-white" data-testid="nearby-radius-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RADIUS_OPTIONS.map((r) => (
                <SelectItem key={r} value={String(r)}>{r} mile{r > 1 ? "s" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => fetchOpportunities(date, radius)} disabled={loading} data-testid="nearby-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {!expanded ? null : (
        <div className="mt-4 space-y-4">
          {/* Route Impact stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="nearby-impact-stats">
            <ImpactTile icon={Users} label="Opportunities" value={impact.added_stops || 0} sub={`${data?.scheduled_jobs_count || 0} jobs on route`} accent="amber" testid="nearby-stat-stops" />
            <ImpactTile icon={DollarSign} label="Added Revenue" value={`$${(impact.added_revenue || 0).toFixed(0)}`} sub="if all accepted" accent="emerald" testid="nearby-stat-revenue" />
            <ImpactTile icon={Fuel} label="Fuel Savings" value={`$${(impact.fuel_savings || 0).toFixed(2)}`} sub={`${(impact.miles_saved || 0).toFixed(1)} mi saved`} accent="emerald" testid="nearby-stat-fuel" />
            <ImpactTile icon={Clock} label="Drive Time Saved" value={`${impact.drive_minutes_saved || 0} min`} sub="vs separate trips" accent="emerald" testid="nearby-stat-time" />
            <ImpactTile icon={TrendingUp} label="Density Score" value={`${impact.density_before || 0} → ${impact.density_after || 0}`} sub={impact.density_delta ? `+${impact.density_delta} pts` : "—"} accent="indigo" testid="nearby-stat-density" />
          </div>

          {/* Neighborhood grouping */}
          {nbhs.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-3" data-testid="nearby-neighborhoods">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-3.5 h-3.5 text-gray-500" />
                <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">By Neighborhood</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {nbhs.map((n) => (
                  <span key={n.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-100 text-[11px] font-bold text-gray-800" data-testid={`nbh-pill-${n.key}`}>
                    <span className="text-emerald-700">{n.label}</span>
                    <span className="text-gray-600">· {n.count} · ${n.added_revenue.toFixed(0)}</span>
                    <span className="text-amber-700">· avg {n.avg_distance_miles}mi</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Opportunity list */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            </div>
          ) : opps.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-6 text-center" data-testid="nearby-empty">
              <AlertCircle className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <div className="text-sm font-bold text-gray-700">No nearby opportunities found</div>
              <div className="text-xs text-gray-500 mt-1">
                {(data?.scheduled_jobs_count || 0) === 0
                  ? "No scheduled jobs on this date yet. Visit Route Map to geocode addresses first."
                  : `No unscheduled customers within ${radius} mi of today's route.`}
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="nearby-opps-list">
              {opps.map((opp) => (
                <OpportunityRow
                  key={opp.user_id}
                  opp={opp}
                  busy={!!scheduling[opp.user_id]}
                  onAdd={() => handleSchedule(opp)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImpactTile({ icon: Icon, label, value, sub, accent = "amber", testid }) {
  const ACCENTS = {
    amber:   "border-amber-200 from-amber-50 to-white text-amber-700",
    emerald: "border-emerald-200 from-emerald-50 to-white text-emerald-700",
    indigo:  "border-indigo-200 from-indigo-50 to-white text-indigo-700",
  };
  const cls = ACCENTS[accent] || ACCENTS.amber;
  return (
    <div className={`bg-gradient-to-br ${cls.split(" ").slice(1, 3).join(" ")} border ${cls.split(" ")[0]} rounded-xl p-2.5`} data-testid={testid}>
      <div className={`flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] font-extrabold ${cls.split(" ").slice(-1)}`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-0.5 text-base font-extrabold tabular-nums tracking-tight font-[Outfit] text-gray-900">{value}</div>
      <div className="text-[10px] text-gray-500 truncate">{sub}</div>
    </div>
  );
}

function OpportunityRow({ opp, busy, onAdd }) {
  const nj = opp.nearest_job || {};
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-start gap-3 hover:border-amber-300 transition-colors" data-testid={`nearby-opp-${opp.user_id}`}>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-100 to-amber-100 text-emerald-800 flex items-center justify-center text-[12px] font-extrabold shrink-0">
        {(opp.name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="text-sm font-extrabold text-gray-900 truncate">{opp.name}</div>
          {opp.is_crm_only && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[9px] font-extrabold uppercase text-indigo-700">CRM</span>
          )}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-[9px] font-extrabold uppercase text-amber-800" data-testid={`opp-nbh-${opp.user_id}`}>
            {opp.neighborhood}
          </span>
        </div>
        <div className="text-[11px] text-gray-600 truncate flex items-center gap-1">
          <MapPin className="w-3 h-3 shrink-0" /> {opp.address || "—"}
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 truncate">
          Nearest stop: <span className="font-bold text-gray-700">{nj.name || "—"}</span> · <span className="font-bold text-emerald-700">{nj.distance_miles?.toFixed?.(1) ?? nj.distance_miles} mi</span> · {nj.preferred_time || "—"}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          <Chip icon={DollarSign} text={`+$${opp.savings.added_revenue.toFixed(0)}`} accent="emerald" testid={`opp-revenue-${opp.user_id}`} />
          <Chip icon={Fuel}       text={`$${opp.savings.fuel_dollars.toFixed(2)} fuel`} accent="emerald" testid={`opp-fuel-${opp.user_id}`} />
          <Chip icon={Clock}      text={`${opp.savings.drive_time_minutes} min saved`} accent="amber" testid={`opp-time-${opp.user_id}`} />
          <Chip icon={MapPin}     text={`${opp.savings.miles_saved.toFixed(1)} mi`} accent="indigo" testid={`opp-miles-${opp.user_id}`} />
        </div>
      </div>
      <Button
        size="sm"
        onClick={onAdd}
        disabled={busy}
        className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-[11px] font-extrabold h-8 shrink-0"
        data-testid={`add-to-route-${opp.user_id}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Add to Route</>}
      </Button>
    </div>
  );
}

function Chip({ icon: Icon, text, accent = "emerald", testid }) {
  const A = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  }[accent];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${A} border text-[10px] font-extrabold uppercase tracking-[0.06em]`} data-testid={testid}>
      <Icon className="w-2.5 h-2.5" /> {text}
    </span>
  );
}
